import type { CreatorCollaborationPackage, CreatorPlacement } from './model';

type HistoryRecord = Record<string, any>;

/** Display only: keep main's order without changing the authoritative quote history. */
export function getCreatorRateHistory(order: HistoryRecord): HistoryRecord[] {
  const records = (value: unknown): HistoryRecord[] =>
    Array.isArray(value)
      ? value.filter(
          (item) => item && typeof item === 'object' && !Array.isArray(item)
        )
      : [];
  return [
    ...records(order?.negotiationHistory).reverse(),
    ...records(order?.ahaPicksOfferHistoryForDisplay)
      .reverse()
      .map((record) => ({ ...record, isAhaPicksOffer: true }))
  ];
}

export type NegotiationHistoryMessage = {
  kind: 'message' | 'note';
  source: 'brand' | 'creator';
  message: string;
};

export function getNegotiationHistoryMessages(
  record: HistoryRecord
): NegotiationHistoryMessage[] {
  const messages: NegotiationHistoryMessage[] = [];
  const message =
    record?.message ?? record?.messageToInfluencer ?? record?.messageToBrand;
  if (typeof message === 'string' && message.trim()) {
    messages.push({
      kind: 'message',
      source:
        String(
          record?.submitedBy ?? record?.submittedBy ?? ''
        ).toLowerCase() === 'ads'
          ? 'brand'
          : 'creator',
      message
    });
  }
  const note = record?.note?.message;
  if (typeof note === 'string' && note.trim()) {
    messages.push({ kind: 'note', source: 'brand', message: note });
  }
  return messages;
}

const numberOrMissing = (value: unknown): number =>
  value === undefined || value === null || value === '' ? NaN : Number(value);

/** V2 amount is net. The fee breakdown is frozen at the time of the offer. */
export function getNegotiationHistoryMoney(record: HistoryRecord, v2: boolean) {
  const amount = numberOrMissing(record.amount);
  const dealPrice = v2
    ? numberOrMissing(record.dealPrice)
    : numberOrMissing(record.amount ?? record.dealPrice ?? record.price);
  const serviceFee = numberOrMissing(
    record.serviceFee ?? (v2 ? undefined : Math.round(dealPrice * 5) / 100)
  );
  const creatorReceives = v2
    ? amount
    : numberOrMissing(record.actualGet ?? dealPrice - serviceFee);
  const serviceFeeRate = numberOrMissing(
    record.serviceFeeRate ??
      (Number.isFinite(serviceFee) && dealPrice > 0
        ? serviceFee / dealPrice
        : undefined)
  );
  return { dealPrice, serviceFee, creatorReceives, serviceFeeRate };
}

export type NegotiationHistoryPlacement = {
  placement: CreatorPlacement;
  amount: number;
  removed: boolean;
};

/** Never use today's channel membership or prices to reconstruct a V2 snapshot. */
export function getNegotiationHistoryPlacements(
  record: HistoryRecord,
  collaborationPackage: CreatorCollaborationPackage
): NegotiationHistoryPlacement[] {
  if (Array.isArray(record.channels)) {
    return record.channels.map((channel: HistoryRecord, index: number) => {
      const id = String(channel.channelKey ?? `history-channel-${index}`);
      const current = collaborationPackage.placements.find(
        (placement) => (placement.channelKey || placement.id) === id
      );
      return {
        placement: {
          id,
          channelKey: id,
          platform: String(
            channel.platform ?? current?.platform ?? ''
          ).toLowerCase(),
          contentType:
            channel.contentType ?? channel.contentGenre ?? current?.contentType,
          userName: String(
            channel.displayName ?? current?.userName ?? ''
          ).replace(/^@/, ''),
          raw: {
            ...channel,
            profileLink: channel.profileLink || current?.raw?.profileLink
          }
        },
        amount: numberOrMissing(channel.amount),
        removed: channel.removed === true
      };
    });
  }
  if (Array.isArray(collaborationPackage.raw?.channels)) return [];
  const total = collaborationPackage.placements.reduce(
    (sum, placement) =>
      sum + Number(placement.originalRate ?? placement.rate ?? 0),
    0
  );
  const offers: HistoryRecord[] = Array.isArray(record.placementOffers)
    ? record.placementOffers
    : collaborationPackage.placements.map((placement) => ({
        placementId: placement.id,
        amount:
          total > 0
            ? (Number(placement.originalRate ?? placement.rate ?? 0) / total) *
              Number(record.amount ?? 0)
            : 0
      }));
  return offers.flatMap((offer) => {
    const placement = collaborationPackage.placements.find(
      (item) => item.id === offer.placementId
    );
    return placement
      ? [{ placement, amount: numberOrMissing(offer.amount), removed: false }]
      : [];
  });
}
